import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import {
    ColSpanGridParams,
    GridRenderCellParams,
} from '@/global/components/DataGrid/utils/types'
import { FormField } from '@/global/components/Form/TextField'
import IconeAjuda from '@/global/components/IconeAjuda'
import Menu from '@/global/components/MenuV2'
import { SelectEmpresa } from '@/global/components/Selects'
import ContentGuide from '@/global/guides/ContentGuide'
import { useStore } from '@/store'
import { chunk, hoursToMinutes, minutesToHours, normalize } from '@acme/common'
import { SN } from '@acme/core'
import { GitCompareArrowsIcon } from 'lucide-react'
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Empresa } from 'service/api'
import { match } from 'ts-pattern'
import { diasTabelaHorario } from '../constants/dias-tabela-horario'
import { useCanSubmit } from '../context/CanSubmitContext'

const diasSemana = Array.from({ length: 7 }).map((_, index) => ({
    dia: diasTabelaHorario[index],
    index,
}))

export function HorariosForm() {
    const empresa = useStore(state => state.company)
    const { getValues, setValue, watch } = useFormContext()

    const lastUpdate = watch('updatedAt')

    useEffect(() => {
        if (!empresa) return

        const calculateAllDays = async () => {
            // Calcula todos os 7 dias em paralelo
            const dayCalculations = await Promise.all(
                Array.from({ length: 7 }).map(async (_, index) => {
                    const horarios = getValues(
                        Array.from({ length: 3 }).flatMap((_, i) =>
                            ['Ent', 'Sai'].map(
                                prefix =>
                                    `horarios[${index}].${prefix}${i + 1}`,
                            ),
                        ),
                    ).filter(Boolean)
                    const totalReduzido = await Promise.resolve(
                        calcularReducao(empresa, ...horarios),
                    )
                    setValue(`horarios[${index}].totReduzido`, totalReduzido)
                    return { index, totalReduzido }
                }),
            )

            // Calcula o total semanal após todos os dias serem processados
            const totaisReducao = dayCalculations.map(
                ({ totalReduzido }) => totalReduzido,
            )
            const reducaoSemanal = totaisReducao.reduce(
                (acc, curr) => acc + hoursToMinutes(curr),
                0,
            )

            setValue(`totReduzido`, minutesToHours(reducaoSemanal))
        }

        calculateAllDays()
    }, [empresa?.id, lastUpdate])

    return (
        <ContentGuide.DataGrid
            className="rounded-sm border *:border-0 bg-foreground"
            loading={false}
            columns={[
                {
                    ...optionsColumn,
                    field: 'dia',
                    headerName: 'Dia',
                    flex: 1,
                    renderCell: ({ value, row }) =>
                        row.index === 7 ? null : (
                            <span className="select-none">{value}</span>
                        ),
                    disableColumnMenu: true,
                },
                ...Array.from({ length: 3 }).flatMap((_, i) =>
                    ['Entrada', 'Saída'].map(es => ({
                        ...optionsColumn,
                        field: `${normalize(es).slice(0, 3)}${i + 1}`,
                        headerName: es,
                        headerAlign: 'center' as const,
                        align: 'center' as const,
                        width: 90,
                        colSpan: (params: ColSpanGridParams<any, any>) => {
                            if (
                                params.row.index === 7 &&
                                es === 'Entrada' &&
                                i === 2
                            )
                                return 2
                            return 1
                        },
                        renderCell: (params: GridRenderCellParams) => {
                            if (params.row.index === 7) {
                                if (params.field === 'Ent3')
                                    return (
                                        <span
                                            className="select-none w-full text-right"
                                            title="Total semanal"
                                        >
                                            Total semanal
                                        </span>
                                    )
                                return ''
                            }

                            return <CellEntradaSaida {...params} />
                        },
                        disableColumnMenu: true,
                    })),
                ),
                {
                    ...optionsColumn,
                    align: 'center',
                    headerAlign: 'center',
                    field: 'totSem',
                    headerName: 'Total',
                    width: 95,
                    renderCell: ({ row }) => <CellTotal index={row.index} />,
                    disableColumnMenu: true,
                },
                {
                    ...optionsColumn,
                    align: 'center',
                    headerAlign: 'center',
                    field: 'totReduzido',
                    headerName: 'Reduzido',
                    width: 95,
                    renderCell: ({ row }) => (
                        <CellTotalReduzido index={row.index} />
                    ),
                    disableColumnMenu: true,
                },
                {
                    ...optionsColumn,
                    field: '__actions',
                    type: 'actions',
                    width: 45,
                    headerAlign: 'center',
                    align: 'center',
                    renderHeader: () => (
                        <IconeAjuda
                            className="size-full mx-auto"
                            Icon={<GitCompareArrowsIcon />}
                        >
                            <div className="w-72">
                                <p>
                                    Você está visualizando a redução conforme
                                    configurado na empresa
                                </p>
                                <div className="w-full pt-4 pb-6">
                                    <SelectEmpresa width="100%" />
                                </div>
                                <table className="text-base border-collapse font-normal">
                                    <tbody className="*:divide-x-8 *:divide-transparent">
                                        <tr>
                                            <td>Utiliza redução?</td>
                                            <td className="font-bold">
                                                {SN.format(
                                                    empresa?.reducaoHrsATrabalhar,
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Início do horário noturno</td>
                                            <td className="font-bold">
                                                {
                                                    empresa?.horarioInicioAdicionalNoturno
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Fim do horário noturno</td>
                                            <td className="font-bold">
                                                {
                                                    empresa?.horarioFimAdicionalNoturno
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </IconeAjuda>
                    ),
                    getActions: ({ row }) =>
                        row.index === 7
                            ? []
                            : [
                                  <RepetirHorariosItem index={row.index} />,
                                  <LimparHorariosItem index={row.index} />,
                              ],
                },
            ]}
            rows={[
                ...diasSemana,
                {
                    dia: '',
                    index: 7,
                },
            ].map(it => ({ ...it, id: it.index }))}
            ToolbarProps={{ hideToolbar: true }}
            slots={{ Footer: () => null }}
        />
    )
}

const CellEntradaSaida = React.memo(function CellEntradaSaida({
    row: { index },
    field,
}: GridRenderCellParams<any, { dia: string; index: number }>) {
    const { setValue, getValues } = useFormContext()
    const { setCanSubmit } = useCanSubmit()

    function onBlur(field: any) {
        return function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
            const value = e.currentTarget.value || ''
            const hora = match(value.length)
                .with(0, () => '')
                .with(1, () => `0${value}:00`)
                .otherwise(() => value.padEnd(5, '0'))
            setValue(field, hora)

            const horariosDia: string[] = getValues(
                Array.from({ length: 3 }).flatMap((_, i) =>
                    ['Ent', 'Sai'].map(
                        prefix => `horarios[${index}].${prefix}${i + 1}`,
                    ),
                ),
            ).filter(Boolean)
            if (horariosDia.length % 2 !== 0) horariosDia.pop()

            const totalDia = calcularReducao(
                { reducaoHrsATrabalhar: 'N' },
                ...horariosDia,
            )
            setValue(`horarios[${index}].Tot`, totalDia)
            setValue('updatedAt', new Date().getTime())

            setCanSubmit(true)
        }
    }

    const onFocus = () => setCanSubmit(false)

    return (
        <FormField
            variant="outlined"
            name={`horarios[${index}].${field}`}
            size="small"
            type="time"
            unHelperText
            onBlur={onBlur(`horarios[${index}].${field}`)}
            onFocus={onFocus}
        />
    )
})

const RepetirHorariosItem = React.memo(function RepetirHorariosItem({
    index,
}: {
    index: number
}) {
    const { getValues, setValue } = useFormContext()
    return (
        <Menu.Item
            key="repetir"
            onClick={() => {
                const horario = getValues(`horarios[${index}]`)

                Array.from({ length: 7 }).forEach((_, i) => {
                    if (i !== index) {
                        setValue(`horarios[${i}]`, {
                            ...horario,
                            label: diasSemana[i].dia,
                        })
                    }
                })
                setValue('updatedAt', new Date().getTime())
            }}
        >
            <Menu.Item.Text>Repetir horários</Menu.Item.Text>
        </Menu.Item>
    )
})

const LimparHorariosItem = React.memo(function LimparHorariosItem({
    index,
}: {
    index: number
}) {
    const { setValue } = useFormContext()

    return (
        <Menu.Item
            key="limpar"
            onClick={() => {
                setValue(`horarios[${index}]`, {
                    label: diasSemana[index].dia,
                    Ent1: '',
                    Sai1: '',
                    Ent2: '',
                    Sai2: '',
                    Ent3: '',
                    Sai3: '',
                    Tot: '00:00',
                    totReduzido: '00:00',
                })
                setValue('updatedAt', new Date().getTime())
            }}
        >
            <Menu.Item.Text>Limpar horários</Menu.Item.Text>
        </Menu.Item>
    )
})

const CellTotal = React.memo(function CellTotal({ index }: { index: number }) {
    const { watch, setValue } = useFormContext()

    const totais = watch(
        Array.from({ length: 7 }).map((_, i) => `horarios[${i}].Tot`),
    )

    useEffect(() => {
        if (index !== 7) return
        const totalSemanal = totais.reduce((acc, curr) => {
            if (curr) acc += hoursToMinutes(curr)
            return acc
        }, 0)

        setValue(`totSem`, minutesToHours(totalSemanal))
    }, totais)

    if (index === 7)
        return (
            <FormField
                variant="outlined"
                name="totSem"
                disabled
                size="small"
                unHelperText
            />
        )
    return (
        <FormField
            variant="outlined"
            name={`horarios[${index}].Tot`}
            disabled
            size="small"
            type="time"
            unHelperText
        />
    )
})

const CellTotalReduzido = React.memo(function CellTotalReduzido({
    index,
}: {
    index: number
}) {
    if (index === 7)
        return (
            <FormField
                variant="outlined"
                name="totReduzido"
                disabled
                size="small"
                unHelperText
            />
        )
    return (
        <FormField
            variant="outlined"
            name={`horarios[${index}].totReduzido`}
            disabled
            size="small"
            type="time"
            unHelperText
        />
    )
})

function calcularReducao(
    empresa: Pick<
        Empresa,
        | 'reducaoHrsATrabalhar'
        | 'horarioInicioAdicionalNoturno'
        | 'horarioFimAdicionalNoturno'
    >,
    ...horarios: string[]
) {
    if (horarios.length % 2 !== 0) horarios.pop()
    if (!horarios.length) return '00:00'

    let inicioNoturno = empresa.horarioInicioAdicionalNoturno,
        fimNoturno = empresa.horarioFimAdicionalNoturno
    if (!inicioNoturno || !fimNoturno) {
        inicioNoturno = '00:00'
        fimNoturno = '00:00'
    }

    const pares = chunk(horarios, 2),
        inicioNoturnoMinutos = hoursToMinutes(inicioNoturno),
        fimNoturnoMinutos = hoursToMinutes(fimNoturno),
        fatorReducao = SN.isFalse(empresa.reducaoHrsATrabalhar) ? 1 : 60 / 52.5

    // Função para calcular diferença de tempo considerando turnos que atravessam meia-noite
    function calculateWorkTime(entrada: number, saida: number): number {
        if (saida >= entrada) {
            // Turno normal (não atravessa meia-noite)
            return saida - entrada
        } else {
            // Turno que atravessa meia-noite
            const minutesInDay = hoursToMinutes('24:00')
            return minutesInDay - entrada + saida
        }
    }

    // Função para calcular sobreposição entre dois períodos, considerando atravessar meia-noite
    function calculateOverlap(
        workStart: number,
        workEnd: number,
        nightStart: number,
        nightEnd: number,
    ): number {
        // Se não há período noturno configurado
        if (nightStart === 0 && nightEnd === 0) {
            return 0
        }

        // Normalizar os horários para lidar com períodos que atravessam meia-noite
        const minutesInDay = hoursToMinutes('24:00')

        // Se o trabalho atravessa meia-noite, normalizamos
        if (workEnd < workStart) {
            workEnd += minutesInDay
        }

        // Se o período noturno atravessa meia-noite, normalizamos
        if (nightEnd < nightStart) {
            nightEnd += minutesInDay
        }

        // Se o trabalho atravessa meia-noite mas o período noturno não
        if (workEnd > minutesInDay && nightEnd <= minutesInDay) {
            // Calculamos em duas partes: antes da meia-noite e depois
            const overlapBefore = Math.max(
                0,
                Math.min(minutesInDay, nightEnd) -
                    Math.max(workStart, nightStart),
            )
            const overlapAfter = Math.max(
                0,
                Math.min(workEnd - minutesInDay, nightEnd) -
                    Math.max(0, nightStart),
            )
            return overlapBefore + overlapAfter
        }

        // Se o período noturno atravessa meia-noite mas o trabalho não
        if (nightEnd > minutesInDay && workEnd <= minutesInDay) {
            // Calculamos em duas partes: antes da meia-noite e depois (se o trabalho continuar no dia seguinte)
            const overlapBefore = Math.max(
                0,
                Math.min(workEnd, minutesInDay) -
                    Math.max(workStart, nightStart),
            )
            const overlapAfter = Math.max(
                0,
                Math.min(workEnd, nightEnd - minutesInDay) -
                    Math.max(workStart, 0),
            )
            return overlapBefore + overlapAfter
        }

        // Caso normal: calcular sobreposição direta
        return Math.max(
            0,
            Math.min(workEnd, nightEnd) - Math.max(workStart, nightStart),
        )
    }

    const result = pares.reduce((acc, [entrada, saida]) => {
        if (!entrada || !saida) return acc

        const entradaMinutos = hoursToMinutes(entrada)
        const saidaMinutos = hoursToMinutes(saida)

        // Calcula o total de minutos trabalhados
        const totalTrabalhado = calculateWorkTime(entradaMinutos, saidaMinutos)

        // Calcula quantos minutos foram trabalhados no período noturno
        const minutosNoturnosComReducao = calculateOverlap(
            entradaMinutos,
            saidaMinutos,
            inicioNoturnoMinutos,
            fimNoturnoMinutos,
        )

        // Calcula quantos minutos foram trabalhados fora do período noturno
        const minutosNormais = totalTrabalhado - minutosNoturnosComReducao

        // Aplica a redução apenas aos minutos noturnos
        const totalComReducao =
            minutosNormais + minutosNoturnosComReducao * fatorReducao

        return acc + totalComReducao
    }, 0)

    return minutesToHours(result)
}
